<template>
  <b-card no-body>
    <b-card-body>
      <payments-filter :values="filterValues" :filter-submitting="isLoading" @filter="setFilter" />

      <vue-good-table
        mode="remote"
        :sort-options="{ enabled: false }"
        :total-rows="totalRecords"
        :is-loading.sync="isLoading"
        :show-footer="true"
        :pagination-options="{
          perPage: filter.limit,
          setCurrentPage: filter.page,
          enabled: true,
          jumpFirstOrLast: true,
          rowsPerPageLabel: t('Rows per page'),
          ofLabel: t('ofLabel'),
          nextLabel: t('nextLabel'),
          prevLabel: t('prevLabel'),
          dropdownAllowAll: false
        }"
        :rows="rows"
        :columns="getColumns"
        @on-page-change="onPageChange"
        @on-per-page-change="onPerPageChange"
      >
        <template slot="table-row" slot-scope="props">
          <span v-if="props.column.field === 'order_id'">
            {{ props.row.order_id || '-' }}
          </span>
          <span v-if="props.column.field === 'customer'">
            {{ (props.row.customer && props.row.customer.name) || '-' }}
            <div class="text-muted font-small-3">
              {{ props.row.customer && props.row.customer.email }}
            </div>
          </span>
          <span v-if="props.column.field === 'amount'">
            {{
              formatAmount(props.row.amount) +
              ' ' +
              (props.row.currency === 'TRY' ? 'TL' : props.row.currency)
            }}
          </span>
          <span v-if="props.column.field === 'status'">
            <b-badge :variant="resolveStatusVariant(props.row.status)">
              {{ $t(props.row.status) }}
            </b-badge>
          </span>
          <span v-if="props.column.field === 'actions'">
            <span class="d-flex justify-content-center table-icon-buttons">
              <b-button
                v-if="props.row.status === 'pending'"
                :id="`edit-button-${props.row.id}`"
                variant="primary"
                type="button"
                size="sm"
                @click="goPayWithCreditCard(props.row)"
              >
                <feather-icon icon="CreditCardIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`edit-button-${props.row.id}`"
                  :title="$t('Pay With Credit Card')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>

              <b-button
                v-if="props.row.status === 'pending'"
                :id="`delete-button-${props.row.id}`"
                variant="danger"
                type="button"
                v-has-role="[ROLES.ADMIN]"
                size="sm"
                @click="deletePaymentIntent(props.row.id)"
              >
                <feather-icon icon="XIcon" size="15" />
                <b-tooltip
                  triggers="hover"
                  :target="`delete-button-${props.row.id}`"
                  :title="$t('Delete Payment Intent')"
                  :delay="{ show: 0, hide: 0 }"
                  boundary="viewport"
                />
              </b-button>
            </span>
          </span>
        </template>
      </vue-good-table>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCard, BCardBody, BTooltip, BButton, BBadge } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { VueGoodTable } from 'vue-good-table';
import { helper } from '@core/mixins/helper.js';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import PaymentsFilter from '@/views/pages/payments/PaymentsFilter.vue';
import useService from '@/auth/service/useService';
import { ROLES } from '@/constants/roles';

export default {
  components: {
    VueGoodTable,
    BCard,
    BCardBody,
    BTooltip,
    BButton,
    BBadge,

    // eslint-disable-next-line vue/no-unused-components
    ToastificationContent,
    PaymentsFilter
  },
  mixins: [helper],
  data() {
    return {
      isLoading: false,
      rows: [],
      totalRecords: 0,
      filter: {
        page: 1,
        limit: 10
      },
      filterValues: {
        customer_id: ''
      },
      defaultFilterValues: {
        customer_id: ''
      }
    };
  },
  computed: {
    getColumns() {
      if (!this.rows.length) {
        return [];
      }

      return [
        {
          label: this.$t('ID'),
          field: 'order_id'
        },
        {
          label: this.$t('Customer'),
          field: 'customer'
        },
        {
          label: this.$t('Amount'),
          field: 'amount'
        },
        {
          label: this.$t('Status'),
          field: 'status'
        },
        {
          label: this.$t('Actions'),
          field: 'actions'
        }
      ];
    }
  },
  async created() {
    this.loadItems();
  },
  methods: {
    resolveStatusVariant(status) {
      return (
        {
          completed: 'success',
          processing: 'primary',
          pending: 'warning',
          failed: 'danger'
        }[status] || 'secondary'
      );
    },

    updateParams(newProps) {
      this.filter = { ...this.filter, ...newProps };
    },

    onPageChange(params) {
      this.updateParams({ page: params.currentPage });
      this.loadItems();
    },

    onPerPageChange(params) {
      this.filter.page = 1;
      this.updateParams({ limit: params.currentPerPage });
      this.loadItems();
    },

    loadItems() {
      let requestData = this.filter;

      requestData = {
        ...requestData,
        ...this.getFilteredObject(this.filterValues)
      };

      this.isLoading = true;
      useService.paymentService
        .getPaymentIntentList(requestData)
        .then(response => {
          this.rows = response.data.data;
          this.totalRecords = response.data.total;
        })
        .finally(() => {
          this.isLoading = false;
        });

      return true;
    },
    setFilter(data) {
      this.filterValues = data
        ? { ...this.defaultFilterValues, ...data }
        : { ...this.defaultFilterValues };
      this.filter.page = 1;
      this.loadItems();
    },
    goPayWithCreditCard(paymentIntent) {
      this.$router.push(
        `/payments/${paymentIntent.id}/pay-with-credit-card?amount=${paymentIntent.amount}&currency=${paymentIntent.currency}`
      );
    },
    deletePaymentIntent(id) {
      this.$swal({
        title: `${this.$t('Warning')}!`,
        text: `${this.$t('Delete Payment Intent Warning')}`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: `${this.$t('Yes')}`,
        cancelButtonText: `${this.$t('No')}`,
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1'
        },
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          await useService.paymentService
            .deletePaymentIntent(id)
            .then(response => {
              this.showSuccessMessage(
                `${response.message || this.$t('Delete Payment Intent Success Message')}`
              );

              this.filter.page = 1;
              this.loadItems();
            })
            .catch(error => {
              this.showErrorMessage(error.message);
            });
        }
      });
    }
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t,
      ROLES
    };
  }
};
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
